import React, { useState } from 'react';
import Layout from '../../components/Layout';
import ModuleCard from '../../ui/ModuleCard';
import { ICONS } from '../../ui/icon-map';
import { toast } from '../../ui/feedback';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import LoadingSpinner from '../../components/LoadingSpinner';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { finesService, errorMessage } from '../../services/finesService';

import Reminder from './components/Reminder';
import MileageCalculator from './components/MileageCalculator';
import FuelCalculator from './components/FuelCalculator';
import DiscountTaxCalculator from './components/DiscountTaxCalculator';

// Airport contracts and the E-Check archive come from Ejar through the engine's hourly sync (no upload - owner, 28 Sep 2026).
const TOOLS = [
        { id: 'backup', label: 'Download Backup', icon: ICONS.export, desc: 'Export all data to ZIP', permission: PERMISSIONS.view_ops_backup, group: 'data' },
        { id: 'reminder', label: 'Reminder', icon: ICONS.reminder, desc: 'Contracts 14-days check', permission: PERMISSIONS.view_ops_reminder, group: 'contracts' },
        { id: 'mileage', label: 'Mileage Calculator', icon: ICONS.calculator, desc: 'Calc excess kilometers', permission: PERMISSIONS.view_ops_mileage, group: 'calculators' },
        { id: 'fuel', label: 'Fuel Calculator', icon: ICONS.calculator, desc: 'Fuel surcharge calculator', permission: PERMISSIONS.view_ops_fuel, group: 'calculators' },
        { id: 'discount_calc', label: 'Discount & Tax Calc', icon: ICONS.calculator, desc: 'Professional price tools', permission: PERMISSIONS.view_ops_discount, group: 'calculators' },
];

// Tools grouped by purpose on the portal home.
const GROUPS = [
    { id: 'calculators', title: 'Calculators', description: 'Charges to quote or settle at the counter.' },
    { id: 'contracts', title: 'Contract follow-up', description: 'Collections and refunds that need action.' },
    { id: 'data', title: 'Data', description: 'Exports of the system data.' },
];

const OperationsPortal = () => {
    const { hasPermission } = usePermissions();
    const [isDownloading, setIsDownloading] = useState(false);
    // Initialize state from localStorage to persist selection on refresh
    // (ignore ids of tools that no longer exist, e.g. the removed airport/archive sync)
    // A link can open one tool directly (/operations?tool=fuel); else the last one used on this device. Either only
    // when the user may open that tool - its tile's permission (permissions phase 3, 4 Oct 2026: the link and the
    // device's last tool opened the Reminder without view_ops_reminder).
    const mayOpen = (id) => TOOLS.some((t) => t.id === id && hasPermission(t.permission));
    const [activeTool, setActiveTool] = useState(() => {
        const linked = new URLSearchParams(window.location.search).get('tool');
        if (linked !== 'backup' && mayOpen(linked)) return linked;
        let stored = null;
        try { stored = localStorage.getItem('yelo_ops_active_tool'); } catch (e) { /* storage blocked */ }
        return mayOpen(stored) ? stored : null;
    });
    const tools = TOOLS;

    const handleToolSelect = async (toolId) => {
        if (toolId === 'backup' && USE_CONTRACTS_V2) {
            // on the contracts store the engine builds it from PostgreSQL: every contract and fine, not the open ones
            // the page holds (owner, 4 Oct 2026: "أعمله يجيب من PostgreSQL (كل العقود والمخالفات)")
            setIsDownloading(true);
            try {
                await finesService.backupExport();
                toast.success('Backup downloaded successfully!');
            } catch (error) {
                toast.error(`Backup failed: ${errorMessage(error)}`);
            } finally {
                setIsDownloading(false);
            }
            return;
        }
        if (toolId === 'backup') {
            setIsDownloading(true);
            try {
                const JSZip = (await import('jszip')).default;
                const XLSX = (await import('xlsx'));
                const { archiveService } = await import('../../services/archiveService');
                const { fleetService, staffService, contractsService, invygoService, maintenanceService, airportService } = await import('../../services/firestore');

                const zip = new JSZip();

                const [fleet, staff, contracts, invygo, maintenance, airport, archive, traffic] = await Promise.all([
                    fleetService.getAllCars(true),
                    staffService.getAllStaff(),
                    contractsService.getAllContracts(true),
                    invygoService.getAllInvygoCars(true),
                    maintenanceService.getAllLogs(true),
                    airportService.getAllAirportContracts(true),
                    archiveService.load(),
                    // the traffic fines are the engine's (in its nightly backup bundle) - Firestore holds a frozen copy
                    // no browser reads any more (owner, 5 Oct 2026: no billed Firestore reads)
                    Promise.resolve([])
                ]);

                // Save to local JSON files
                const saveToPublic = async (filename, data) => {
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    link.click();
                    URL.revokeObjectURL(url);
                };

                if (airport && airport.length > 0) {
                    saveToPublic('airport_data.json', airport);
                }
                if (traffic && traffic.length > 0) {
                    saveToPublic('traffic_data.json', traffic);
                }

                const datasets = [
                    { name: 'Fleet', data: fleet },
                    { name: 'Staff', data: staff },
                    { name: 'Contracts', data: contracts },
                    { name: 'Invygo', data: invygo },
                    { name: 'Maintenance', data: maintenance },
                    { name: 'Airport', data: airport },
                    { name: 'Archive_History', data: archive },
                    { name: 'Traffic_Fines', data: traffic }
                ];

                datasets.forEach(({ name, data }) => {
                    if (data && data.length > 0) {
                        const ws = XLSX.utils.json_to_sheet(data);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, name);
                        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                        zip.file(`${name}.xlsx`, excelBuffer);
                    }
                });

                const content = await zip.generateAsync({ type: 'blob' });
                const url = URL.createObjectURL(content);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Yelo_Backup_${new Date().toISOString().split('T')[0]}.zip`;
                link.click();
                URL.revokeObjectURL(url);

                toast.success('Backup downloaded successfully!');
            } catch (error) {
                toast.error('Backup failed: ' + error.message);
            } finally {
                setIsDownloading(false);
            }
            return;
        }
        setActiveTool(toolId);
        localStorage.setItem('yelo_ops_active_tool', toolId);
    };

    const renderTool = () => {
        if (!mayOpen(activeTool)) return null;     // e.g. "show the app as this role" switched to a role without it
        switch (activeTool) {
            case 'reminder':
                return <Reminder />;
            case 'mileage':
                return <MileageCalculator />;
            case 'fuel':
                return <FuelCalculator />;
            case 'discount_calc':
                return <DiscountTaxCalculator />;

            default:
                return null;
        }
    };

    const handleBack = () => {
        setActiveTool(null);
        localStorage.removeItem('yelo_ops_active_tool');
    };

    const visibleGroups = GROUPS
        .map((g) => ({ ...g, tools: tools.filter((t) => t.group === g.id && hasPermission(t.permission)) }))
        .filter((g) => g.tools.length > 0);

    return (
        <Layout
            title={activeTool ? tools.find(t => t.id === activeTool)?.label : 'Operations Portal'}
            subtitle={activeTool ? undefined : 'Counter tools for daily operations'}
            backPath={activeTool ? handleBack : null}>
            {isDownloading && <LoadingSpinner message="Preparing backup..." />}
            {activeTool ? (
                // the Reminder / day-13 collections table is wide: give it the whole width
                <div className={`w-full mx-auto ${activeTool === 'reminder' ? '' : 'max-w-7xl'}`}>
                    {renderTool()}
                </div>
            ) : (
                <div className="w-full max-w-6xl mx-auto space-y-8">
                    {visibleGroups.map((group) => (
                        <section key={group.id} aria-labelledby={`ops-group-${group.id}`}>
                            <div className="mb-3">
                                <h2 id={`ops-group-${group.id}`} className="text-sm font-semibold text-gray-900">{group.title}</h2>
                                <p className="text-xs text-gray-500">{group.description}</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {group.tools.map((tool) => (
                                    <ModuleCard
                                        key={tool.id}
                                        title={tool.label}
                                        desc={tool.desc}
                                        iconGlyph={tool.icon}
                                        onClick={() => handleToolSelect(tool.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </Layout>
    );
};

export default OperationsPortal;
